import Table from '@/components/Table'
import { TableData } from '@/components/Table/type'
import { useOperationQuery } from '@/queries/useOperationQuery'
import { Operation } from '@/types/Operation'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'
import DadosMestreHeader from '../components/DadosMestreHeader' 
import DadosMestreHeaderActions from '../components/DadosMestreHeaderActions' 
import DefaultColumn from '@/components/Table/components/DefaultColumn'
import { useTable } from '@/hooks/useTable'
import DeleteDialog from './components/DeleteDialog'
import EditDialog from './components/EditDialog'
import AddDialog from './components/AddDialog'
import TableRowActions from '@/components/Table/components/TableRowActions' 
import { useBaseStore } from '@/store'
import { Store } from '@/store/type'
import Segment from './components/Segment'
import { Split } from 'lucide-react'
import { format } from 'date-fns'

const stateSelector = (state: Store) => ({
    isDetailOpen: state.segmentSlice.state.isDetailOpen,
    setDetailOpen: state.segmentSlice.actions.setDetailOpen
})

export type OperationTable = TableData<Operation>

const columnHelper = createColumnHelper<Operation>()

const MasterDataOperation= () => {
    const {
        table, 
        selectedRow,
        selectedRows,
        isAddDialogOpen,
        isDeleteDialogOpen,
        isEditDialogOpen, 
        onAddDialogClose,
        onClickAdd,
        onClickDelete,
        onClickEdit,
        onClickMultiDelete,
        onDeleteDialogClose,
        onEditDialogClose, 
        setSelectedRow,
        setTable,
        onEditDialogOpen,
    } = useTable<Operation>('')
   
    const { isDetailOpen, setDetailOpen } = useBaseStore(stateSelector)

    const { data, isFetching, isPending, refetch } = useOperationQuery()
    
    const memoData = useMemo(() => data || [], [data])
    const memoColumns = useMemo(
        () => [  
            columnHelper.accessor('SK_OPERACAO', {
                id: 'SK_OPERACAO',
                header: '#',
                size: 70,
            }),
            columnHelper.accessor('NM_RAZAO_SOCIAL', {
                id: 'NM_RAZAO_SOCIAL',
                header: 'Fornecedor - Origem',
                size: 400,
            }),
            columnHelper.accessor('DD_CNPJ_FORNECEDOR', {
                id: 'DD_CNPJ_FORNECEDOR',
                header: 'CNPJ Fornecedor',
                size: 150,
            }),
            columnHelper.accessor(({ SK_EMPRESA_DESTINO, EMPRESA_DESTINO}) => {
                return `${SK_EMPRESA_DESTINO} - ${EMPRESA_DESTINO}`
            }, { 
                id: 'EMPRESA_DESTINO',
                header: 'Cliente - Destino',
                size: 200,
            }),
            columnHelper.accessor('DD_NUMERO_ROTA', {
                id: 'DD_NUMERO_ROTA',
                header: 'Numero Rota',
                size: 130,
            }),  
            columnHelper.accessor('DD_TEMPO_OPERACAO', {
                id: 'DD_TEMPO_OPERACAO',
                header: 'Tempo de operação',
                size: 160,
            }),  
            columnHelper.accessor('DD_LETRA_PEDIDO', {
                id: 'DD_LETRA_PEDIDO',
                header: 'Letra Pedido',
                size: 120,
            }),  
            columnHelper.accessor(({ DT_VIGENCIA_INICIAL }) =>
                DT_VIGENCIA_INICIAL
                    ? format(new Date(`${DT_VIGENCIA_INICIAL} 00:00:00`), 'dd/MM/yyyy')
                    : '', {
                sortingFn: (rowA, rowB, _columnId) => {  
                    const statusA = Number(rowA.original[_columnId])
                    const statusB = Number(rowB.original[_columnId])
                    return statusA < statusB ? 1 : -1
                },
                id: 'DT_VIGENCIA_INICIAL',
                header: 'Vigencia Inicial',
                size: 140,
            }),  
            columnHelper.accessor(({ DT_VIGENCIA_FINAL }) =>
                DT_VIGENCIA_FINAL
                    ? format(new Date(`${DT_VIGENCIA_FINAL} 00:00:00`), 'dd/MM/yyyy')
                    : '', {
                sortingFn: (rowA, rowB, _columnId) => {  
                    const statusA = Number(rowA.original[_columnId])
                    const statusB = Number(rowB.original[_columnId])
                    return statusA < statusB ? 1 : -1
                },
                id: 'DT_VIGENCIA_FINAL',
                header: 'Vigencia Final',
                size: 140,
            }),  
            columnHelper.display({
                id: 'Ações',
                cell: ({ row }) => { 
                    return (
                        (
                            <TableRowActions
                                onClickDelete={() => onClickDelete(row)}
                                onClickEdit={() => onClickEdit(row)}
                                actions={
                                    [{label: 'Trechos', action: () => {
                                        setSelectedRow(row)
                                        setDetailOpen(true) 
                                    }, icon: <Split size={14}/>}]
                                }
                            />
                        )
                    )
                },
                size: 60,
                enableColumnFilter: false,
                enableSorting: false,
            }),
        ],
        []
    )

    return (
        <div className="w-full h-full p-4 overflow-hidden flex">
            <Table<OperationTable>
                data={memoData}
                columns={memoColumns}
                getRowId={(row) => row.SK_OPERACAO.toString()}
                isLoading={isPending}
                isFetching={isFetching}
                getTableInstance={(table) => setTable(table)} 
                tableHeader={
                    <DadosMestreHeader
                        title={`Operações`}
                        onDelete={onClickMultiDelete}
                        selectedRows={
                            table
                                ?.getFilteredRowModel()
                                .flatRows.filter((row) => selectedRows[row.id])
                                .length
                        }
                    />
                }
                tableActions={
                    <DadosMestreHeaderActions 
                        onRefech={refetch}
                        onAdd={onClickAdd}
                    />
                } 
                defaultColumn={{
                    cell: ({ getValue }) => (
                        <DefaultColumn>{getValue() as string}</DefaultColumn>
                    ),
                }}
            /> 
            {isDeleteDialogOpen && (
                <DeleteDialog
                    ids={
                        selectedRow
                            ? [selectedRow.original.SK_OPERACAO]
                            : table
                            ? table
                                  .getFilteredRowModel()
                                  .flatRows.filter(
                                      (row) => selectedRows[row.id]
                                  )
                                  .map((row) => row.original.SK_OPERACAO)
                            : []
                    }
                    isOpen={isDeleteDialogOpen}
                    onClose={onDeleteDialogClose}
                />
            )}
            {selectedRow && (
                <EditDialog
                    row={selectedRow}
                    isOpen={isEditDialogOpen}
                    onClose={onEditDialogClose}
                />
            )}
            <AddDialog isOpen={isAddDialogOpen} onClose={onAddDialogClose} />
            {isDetailOpen &&
                <Segment 
                    id={
                        selectedRow
                            ? selectedRow.original.SK_OPERACAO
                            : 0 
                    } 
                    onEditDialogOperation={() => onEditDialogOpen()}
                />
            }
        </div>
    )
}

export default MasterDataOperation
